import { Tabs } from '@own/fds-react';
import { TabProps } from '@own/fds-react/dist/types/tabs.types';
import React, { useContext, useEffect, useState } from 'react';
import {
    AuthenticationState,
    useAuthentication,
} from '../../../hooks/use-authentication';
import FMALogin, {
    RedirectData,
} from '../../../support/components/FMALogin/FMALogin';
import VINSelector from '../../sections/vehicle-selector-fds/vin-selector/vin-selector';
import YMMSelector from '../../sections/vehicle-selector-fds/ymm-selector/ymm-selector';
import { Heading } from '../heading/heading';
import './vehicle-selector-tabs.scss';
import { useBreakpoints } from '../../../hooks/use-breakpoints';
import siteMapService from '../../../support/services/site-map-service/site-map-service';
import ServerContext from '../../../contexts/serverContext';
import { generateTabsArray } from './util/vehicle-selector-tabs-util';
import { useRecallsContent } from '../../../support/pages/recalls/hook/use-recalls-content';

export interface VehicleSelectorTabsLabels {
    vehicleSelectorTabsTitle: string;
    vinTabLabelDesktop: string | undefined;
    vinTabLabelMobile: string | undefined;
    vinInputBoxLabel: string | undefined;
    vinInputBoxPlaceHolder: string | undefined;
    findMyVinLabel: string | undefined;
    findMyVinLink: string | undefined;
    ymTabLabelDesktop: string | undefined;
    ymTabLabelMobile: string | undefined;
    yearDropdownLabel: string | undefined;
    modelDropdownLabel: string | undefined;
    submitSearchLabel: string | undefined;
    garageTabLabel: string | undefined;
}

interface Props {
    redirectData: RedirectData;
    content: VehicleSelectorTabsLabels;
    handleVinChange?: (vin: string) => void;
    ymmSubmitEvent?: () => void;
}

const VehicleSelectorTabs = (props: Props) => {
    const serverContext = useContext(ServerContext);
    const recallsContent = useRecallsContent();
    const { content, redirectData } = props;
    const [authenticationState] = useAuthentication();
    const [initialTab, setInitialTab] = useState<number>(0);
    const { desktopOrLarger } = useBreakpoints();
    const vin = (optionalRedirect?: RedirectData): TabProps => {
        const vinComponent = {
            title: desktopOrLarger
                ? content?.vinTabLabelDesktop || ''
                : content?.vinTabLabelMobile || '',

            children: (
                <>
                    {(props.redirectData.id === 'recalls' ||
                        'recalls-details') &&
                        serverContext.brand === 'lincoln' &&
                        recallsContent?.recallsInformationUpToThisDateText && (
                            <div className="recalls-info-up-to-this-date-text">
                                {
                                    recallsContent?.recallsInformationUpToThisDateText
                                }
                            </div>
                        )}
                    <VINSelector
                        vinPlaceholderLabel={
                            content?.vinInputBoxPlaceHolder || ''
                        }
                        submitLabel={content?.submitSearchLabel || ''}
                        findMyVinLink={content?.findMyVinLink || ''}
                        findMyVinLabel={content?.findMyVinLabel || ''}
                        isTabs={true}
                        redirectData={optionalRedirect || redirectData}
                        altLabel={content?.vinInputBoxLabel || ''}
                        handleVinSubmit={props.handleVinChange}
                    />
                </>
            ),
            className: 'vin-selector-tab',
        };
        return vinComponent;
    };

    const ymm = (): TabProps => {
        const ymComponent = {
            title: desktopOrLarger
                ? content?.ymTabLabelDesktop || ''
                : content?.ymTabLabelMobile || '',

            children: (
                <YMMSelector
                    yearDropdownLabel={content?.yearDropdownLabel || ''}
                    modelDropdownLabel={content?.modelDropdownLabel || ''}
                    submitLabel={content?.submitSearchLabel || ''}
                    handleDropDownChange={undefined}
                    redirectData={redirectData}
                    isTabs={true}
                    ymmSubmitEvent={props.ymmSubmitEvent}
                />
            ),
            className: 'ymm-selector-tab',
        };
        return ymComponent;
    };

    const garage = (optionalRedirectData?: RedirectData): TabProps => {
        const garageComponent = {
            title: content?.garageTabLabel || '',
            children: (
                <FMALogin
                    redirectData={optionalRedirectData || redirectData}
                    isTabs={true}
                    handleVinChange={props.handleVinChange}
                />
            ),
            className: 'garage-selector-tab',
        };
        return garageComponent;
    };

    const ownerManualVinRedirectData = {
        path: siteMapService.getPathFromRouterConfigs(
            serverContext.currentLanguageRegionCode,
            serverContext.brand,
            'OwnerManualWithVin',
            []
        ),
        id: redirectData.id,
    };

    const tabs = (): TabProps[] => {
        return generateTabsArray(
            redirectData.id,
            content,
            serverContext,
            vin,
            ymm,
            garage,
            ownerManualVinRedirectData
        );
    };
    const initialIndex = (): number => {
        if (authenticationState === AuthenticationState.Authenticated) {
            const currentTabs = tabs();
            const initialIndex = currentTabs.findIndex(
                tab => tab.className === 'garage-selector-tab'
            );
            return initialIndex === -1 ? 0 : initialIndex;
        } else return 0;
    };
    useEffect(() => {
        setInitialTab(initialIndex());
    }, [authenticationState]);
    useEffect(() => {
        setInitialTab(initialIndex());
    }, [serverContext.languageRegionCode]);
    return (
        <>
            {content?.vehicleSelectorTabsTitle && (
                <div className="vehicle-selector-tabs-container">
                    <div className="vehicle-selector-tabs-border">
                        <Heading
                            type="h2"
                            id="vehicle-selector-tabs-heading"
                            className={`vehicle-selector-tabs-heading`}
                        >
                            {content?.vehicleSelectorTabsTitle}
                        </Heading>

                        <div key={`${initialTab} ${desktopOrLarger}`}>
                            <Tabs
                                tabs={tabs()}
                                activeTabIndex={initialTab}
                                className="vehicle-selector-tabs"
                                onChange={setInitialTab}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
export default VehicleSelectorTabs;
